import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../hooks/useNotification';
import { debtorService } from '../services/debtorService';
import Layout from '../components/layout/Layout';
import StatCard from '../components/common/StatCard';
import Button from '../components/common/Button';
import LoadingSpinner from '../components/common/LoadingSpinner';
import {
  FiDollarSign,
  FiShoppingCart,
  FiUsers,
  FiTrendingUp,
  FiDownload,
  FiSearch,
  FiFilter,
  FiClock
} from 'react-icons/fi';

const LendingHistoryPage = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();

  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Quick Search
  const [quickSearch, setQuickSearch] = useState('');

  // Advanced Search
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [transactionType, setTransactionType] = useState('all');

  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showExportMenu, setShowExportMenu] = useState(false);

  useEffect(() => {
    loadTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickSearch, customerName, mobileNumber, dateFrom, dateTo, amountMin, amountMax, transactionType, sortBy, sortOrder, currentPage]);

  const loadTransactions = async () => {
    try {
      setLoading(true);

      // Get all customers first
      const debtorsResponse = await debtorService.getAll({ limit: 1000 });
      const allDebtors = debtorsResponse.data.debtors || [];

      // Get all transactions from each debtor
      const allTransactions = [];
      for (const debtor of allDebtors) {
        try {
          const debtorDetails = await debtorService.getById(debtor._id);
          const debtorTransactions = debtorDetails.data.transactions || [];

          // Add debtor info to each transaction
          debtorTransactions.forEach(txn => {
            allTransactions.push({
              ...txn,
              debtorName: debtor.name,
              debtorMobile: debtor.mobile,
              debtorId: debtor._id
            });
          });
        } catch (err) {
          console.error(`Error loading transactions for debtor ${debtor._id}:`, err);
        }
      }

      // Filter transactions
      let filtered = allTransactions;

      // Quick search
      if (quickSearch) {
        filtered = filtered.filter(txn =>
          txn.debtorName.toLowerCase().includes(quickSearch.toLowerCase()) ||
          txn.debtorMobile.includes(quickSearch)
        );
      }

      // Advanced filters
      if (customerName) {
        filtered = filtered.filter(txn =>
          txn.debtorName.toLowerCase().includes(customerName.toLowerCase())
        );
      }
      if (mobileNumber) {
        filtered = filtered.filter(txn =>
          txn.debtorMobile.includes(mobileNumber)
        );
      }
      if (dateFrom) {
        filtered = filtered.filter(txn =>
          new Date(txn.createdAt) >= new Date(dateFrom)
        );
      }
      if (dateTo) {
        filtered = filtered.filter(txn =>
          new Date(txn.createdAt) <= new Date(dateTo)
        );
      }
      if (amountMin) {
        filtered = filtered.filter(txn =>
          txn.amount >= parseFloat(amountMin)
        );
      }
      if (amountMax) {
        filtered = filtered.filter(txn =>
          txn.amount <= parseFloat(amountMax)
        );
      }
      if (transactionType !== 'all') {
        filtered = filtered.filter(txn => txn.type === transactionType);
      }

      // Sort
      filtered.sort((a, b) => {
        const aVal = a[sortBy];
        const bVal = b[sortBy];
        if (sortOrder === 'asc') {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });

      // Calculate stats
      const totalAmount = filtered.reduce((sum, txn) => sum + txn.amount, 0);
      const debtAmount = filtered.filter(t => t.type === 'debt').reduce((sum, t) => sum + t.amount, 0);
      const paymentAmount = filtered.filter(t => t.type === 'payment').reduce((sum, t) => sum + t.amount, 0);
      const uniqueCustomers = new Set(filtered.map(t => t.debtorId)).size;

      setStats({
        totalAmount,
        debtAmount,
        paymentAmount,
        uniqueCustomers,
        totalTransactions: filtered.length
      });

      // Pagination
      const itemsPerPage = 20;
      const total = Math.ceil(filtered.length / itemsPerPage);
      setTotalPages(total);

      const start = (currentPage - 1) * itemsPerPage;
      const end = start + itemsPerPage;
      setTransactions(filtered.slice(start, end));

    } catch (error) {
      showError('Failed to load lending history');
      console.error('Error loading transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setQuickSearch('');
    setCustomerName('');
    setMobileNumber('');
    setDateFrom('');
    setDateTo('');
    setAmountMin('');
    setAmountMax('');
    setTransactionType('all');
    setCurrentPage(1);
  };

  const handleExportExcel = async () => {
    try {
      // Get all filtered transactions (not paginated)
      const debtorsResponse = await debtorService.getAll({ limit: 1000 });
      const allDebtors = debtorsResponse.data.debtors || [];

      const allTransactions = [];
      for (const debtor of allDebtors) {
        try {
          const debtorDetails = await debtorService.getById(debtor._id);
          const debtorTransactions = debtorDetails.data.transactions || [];

          debtorTransactions.forEach(txn => {
            allTransactions.push({
              ...txn,
              debtorName: debtor.name,
              debtorMobile: debtor.mobile,
              debtorId: debtor._id
            });
          });
        } catch (err) {
          console.error(`Error loading transactions for debtor ${debtor._id}:`, err);
        }
      }

      // Apply same filters as display
      let filtered = allTransactions;
      if (quickSearch) {
        filtered = filtered.filter(txn =>
          txn.debtorName.toLowerCase().includes(quickSearch.toLowerCase()) ||
          txn.debtorMobile.includes(quickSearch)
        );
      }
      if (customerName) {
        filtered = filtered.filter(txn =>
          txn.debtorName.toLowerCase().includes(customerName.toLowerCase())
        );
      }
      if (mobileNumber) {
        filtered = filtered.filter(txn => txn.debtorMobile.includes(mobileNumber));
      }
      if (dateFrom) {
        filtered = filtered.filter(txn => new Date(txn.createdAt) >= new Date(dateFrom));
      }
      if (dateTo) {
        filtered = filtered.filter(txn => new Date(txn.createdAt) <= new Date(dateTo));
      }
      if (amountMin) {
        filtered = filtered.filter(txn => txn.amount >= parseFloat(amountMin));
      }
      if (amountMax) {
        filtered = filtered.filter(txn => txn.amount <= parseFloat(amountMax));
      }
      if (transactionType !== 'all') {
        filtered = filtered.filter(txn => txn.type === transactionType);
      }

      // Create CSV
      const csvData = [
        ['Date', 'Time', 'Customer Name', 'Mobile', 'Type', 'Amount (BHD)', 'Balance After (BHD)', 'Notes'],
        ...filtered.map(txn => [
          new Date(txn.createdAt).toLocaleDateString('en-BH'),
          new Date(txn.createdAt).toLocaleTimeString('en-BH', { hour: '2-digit', minute: '2-digit' }),
          txn.debtorName,
          txn.debtorMobile,
          txn.type === 'debt' ? 'Debt' : 'Payment',
          `${txn.type === 'debt' ? '+' : '-'}${txn.amount.toFixed(3)}`,
          txn.balanceAfter?.toFixed(3) || '0.000',
          txn.notes || '-'
        ])
      ];

      const csvContent = csvData.map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lending_history_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      showSuccess('CSV file downloaded successfully!');
      setShowExportMenu(false);
    } catch (error) {
      showError(error.message || 'Failed to export');
    }
  };

  const handleExportCSV = async () => {
    // Same as Excel for now (CSV export)
    handleExportExcel();
  };

  if (loading && transactions.length === 0) {
    return (
      <Layout title="Lending History">
        <LoadingSpinner />
      </Layout>
    );
  }

  return (
    <Layout title="Lending History">
      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={FiShoppingCart}
            value={stats.totalTransactions}
            label="Total Transactions"
            bgColor="bg-pastel-lavender"
            iconColor="text-white"
            iconBgColor="bg-primary-500"
          />
          <StatCard
            icon={FiDollarSign}
            value={`BHD ${stats.debtAmount.toFixed(3)}`}
            label="Total Lending"
            bgColor="bg-pastel-pink"
            iconColor="text-white"
            iconBgColor="bg-danger-500"
          />
          <StatCard
            icon={FiTrendingUp}
            value={`BHD ${stats.paymentAmount.toFixed(3)}`}
            label="Total Payments"
            bgColor="bg-pastel-mint"
            iconColor="text-white"
            iconBgColor="bg-success-500"
          />
          <StatCard
            icon={FiUsers}
            value={stats.uniqueCustomers}
            label="Unique Customers"
            bgColor="bg-pastel-peach"
            iconColor="text-white"
            iconBgColor="bg-warning-500"
          />
        </div>
      )}

      {/* Quick Search */}
      <div className="bg-white rounded-card shadow-card p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-neutral-900">
            {showAdvancedSearch ? 'Advanced Search' : 'Quick Search'}
          </h3>
          <button
            onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
          >
            <FiFilter className="w-4 h-4" />
            {showAdvancedSearch ? 'Quick Search' : 'Advanced Search'}
          </button>
        </div>

        {!showAdvancedSearch ? (
          // Quick Search
          <div className="flex gap-4 items-center">
            <div className="flex-1 relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by customer name or mobile..."
                value={quickSearch}
                onChange={(e) => setQuickSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-button focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div className="relative">
              <Button onClick={() => setShowExportMenu(!showExportMenu)} variant="outline">
                <FiDownload className="w-4 h-4 mr-2 inline" />
                Export
              </Button>
              {showExportMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-card shadow-lg border border-neutral-200 z-10 overflow-hidden">
                  <button
                    onClick={handleExportExcel}
                    className="block w-full text-left px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-100 transition-colors"
                  >
                    Export to Excel
                  </button>
                  <button
                    onClick={handleExportCSV}
                    className="block w-full text-left px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-100 transition-colors"
                  >
                    Export to CSV
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          // Advanced Search
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Customer Name</label>
              <input
                type="text"
                placeholder="Enter customer name"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                className="w-full px-4 py-2 border border-neutral-300 rounded-button focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Mobile Number</label>
              <input
                type="text"
                placeholder="Enter mobile number"
                value={mobileNumber}
                onChange={(e) => setMobileNumber(e.target.value)}
                className="w-full px-4 py-2 border border-neutral-300 rounded-button focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Transaction Type</label>
              <select
                value={transactionType}
                onChange={(e) => setTransactionType(e.target.value)}
                className="w-full px-4 py-2 border border-neutral-300 rounded-button focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="all">All Types</option>
                <option value="debt">Debt</option>
                <option value="payment">Payment</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Date From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full px-4 py-2 border border-neutral-300 rounded-button focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Date To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full px-4 py-2 border border-neutral-300 rounded-button focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Amount Range</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={amountMin}
                  onChange={(e) => setAmountMin(e.target.value)}
                  className="w-full px-4 py-2 border border-neutral-300 rounded-button focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={amountMax}
                  onChange={(e) => setAmountMax(e.target.value)}
                  className="w-full px-4 py-2 border border-neutral-300 rounded-button focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            <div className="md:col-span-2 lg:col-span-3 flex gap-2 justify-end">
              <Button variant="outline" onClick={handleClearFilters}>
                Clear Filters
              </Button>
              <Button onClick={loadTransactions}>
                Apply Filters
              </Button>
            </div>
          </div>
        )}

        {/* Sort Options */}
        <div className="flex gap-2 text-sm mt-4 pt-4 border-t border-neutral-200">
          <span className="text-neutral-600">Sort by:</span>
          <button
            onClick={() => {
              setSortBy('createdAt');
              setSortOrder('desc');
            }}
            className={`px-2 py-1 rounded ${
              sortBy === 'createdAt' && sortOrder === 'desc' ? 'text-primary-600 font-semibold' : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            Latest on Top
          </button>
          <span className="text-neutral-300">|</span>
          <button
            onClick={() => {
              setSortBy('createdAt');
              setSortOrder('asc');
            }}
            className={`px-2 py-1 rounded ${
              sortBy === 'createdAt' && sortOrder === 'asc' ? 'text-primary-600 font-semibold' : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            Oldest First
          </button>
          <span className="text-neutral-300">|</span>
          <button
            onClick={() => {
              setSortBy('amount');
              setSortOrder('desc');
            }}
            className={`px-2 py-1 rounded ${
              sortBy === 'amount' && sortOrder === 'desc' ? 'text-primary-600 font-semibold' : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            Highest Amount
          </button>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-white rounded-card shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                  Date & Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                  Customer Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                  Items/Notes
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                  Balance After
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {transactions.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <FiShoppingCart className="mx-auto h-12 w-12 text-neutral-400 mb-4" />
                    <p className="text-neutral-600 mb-4">No transactions found</p>
                  </td>
                </tr>
              ) : (
                transactions.map((txn) => (
                  <tr key={txn._id} className="hover:bg-neutral-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm">
                        <FiClock className="w-4 h-4 text-neutral-400 mr-2" />
                        <div>
                          <div className="font-medium text-neutral-900">
                            {new Date(txn.createdAt).toLocaleDateString('en-BH')}
                          </div>
                          <div className="text-xs text-neutral-500">
                            {new Date(txn.createdAt).toLocaleTimeString('en-BH', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => navigate(`/debtors/${txn.debtorId}`)}
                        className="text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline"
                      >
                        {txn.debtorName}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                      {txn.debtorMobile}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        txn.type === 'debt'
                          ? 'bg-danger-100 text-danger-800'
                          : 'bg-success-100 text-success-800'
                      }`}>
                        {txn.type === 'debt' ? 'Debt' : 'Payment'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className={`text-sm font-semibold ${
                        txn.type === 'debt' ? 'text-danger-600' : 'text-success-600'
                      }`}>
                        {txn.type === 'debt' ? '+' : '-'} BHD {txn.amount.toFixed(3)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-neutral-600 max-w-xs truncate">
                      {txn.notes || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-neutral-900">
                      BHD {txn.balanceAfter?.toFixed(3) || '0.000'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-neutral-200 flex items-center justify-between">
            <div className="text-sm text-neutral-600">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default LendingHistoryPage;
